<template>
  <Transition
    name="fade"
    mode="out-in"
    enterActiveClass="transition-all duration-150"
    leaveActiveClass="transition-all duration-75"
    enterFromClass="opacity-0"
    leaveToClass="opacity-0"
    enterToClass="opacity-100"
    leaveFromClass="opacity-100"
  >
    <div v-show="show" id="cookie-consent-banner">
      <div id="text">
        <h3>{{ app.texts.cookieconsent.header }}</h3>
        <p>{{ app.texts.cookieconsent.description }}</p>
      </div>

      <button
        id="btn-extra-information"
        @click="showExtraInformation = !showExtraInformation"
      >
        <span>{{ app.texts.cookieconsent.extra }}</span>
        <span class="text-xs">{{ showExtraInformation ? "▲" : "▼" }}</span>
      </button>

      <div v-show="showExtraInformation" id="preferences">
        <div class="preference">
          <label class="opacity-50"
            ><input
              id="consent-functional"
              v-model="consent.necessary"
              type="checkbox"
              value="Necessary"
              disabled
            />{{ app.texts.cookieconsent.functional }}</label
          >
          <div class="description">
            <span class="text-xs">ℹ️ </span>
            <span>{{ app.texts.cookieconsent.functionalDescription }}</span>
          </div>
        </div>
        <div class="preference">
          <label
            ><input
              id="consent-analytics"
              v-model="consent.analytics"
              name="consent-analytics"
              type="checkbox"
              value="Statistics"
            />{{ app.texts.cookieconsent.analytics }}</label
          >
          <div class="description">
            <span class="text-xs">ℹ️ </span>
            <span>{{ app.texts.cookieconsent.analyticsDescription }}</span>
          </div>
        </div>
        <div class="preference">
          <label
            ><input
              id="consent-preferences"
              v-model="consent.preferences"
              type="checkbox"
              value="Preferences"
            />{{ app.texts.cookieconsent.preferences }}</label
          >
          <div class="description">
            <span class="text-xs">ℹ️ </span>
            <span>{{ app.texts.cookieconsent.preferencesDescription }}</span>
          </div>
        </div>
        <div class="preference">
          <label
            ><input
              id="consent-marketing"
              v-model="consent.marketing"
              type="checkbox"
              value="Marketing"
            />{{ app.texts.cookieconsent.marketing }}</label
          >
          <div class="description">
            <span class="text-xs">ℹ️ </span>
            <span>{{ app.texts.cookieconsent.marketingDescription }}</span>
          </div>
        </div>
      </div>

      <div id="buttons">
        <Button id="btn-accept-all" @click="clickAcceptAll">
          {{ app.texts.cookieconsent.acceptAll }}
        </Button>
        <Button id="btn-accept-some" variant="white" @click="clickAcceptSome">
          {{ app.texts.cookieconsent.acceptSelected }}
        </Button>
        <Button id="btn-reject-all" variant="gray" @click="clickRejectAll">
          {{ app.texts.cookieconsent.rejectAll }}
        </Button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { app } from "@/app.context";
import decorateLinks from "@/utils/link_decorator";
import {
  addGTMScript,
  ConsentMode,
  defaultConsent,
  getContextConsent,
  getEncodedConsent,
  getStoreConsent,
  pushEventToDatalayer,
  setStoreConsent,
} from "@/views/components/consent-banner/ConsentBanner.utils";
import { reactive, ref, watch } from "vue";

const props = defineProps<{ tenant: string; gtmCode: string }>();

const show = ref(false);
const showExtraInformation = ref(false);

const consent = reactive({
  necessary: false,
  analytics: false,
  preferences: false,
  marketing: false,
});

watch(
  show,
  (value) => {
    if (value) {
      consent.necessary = true;
    }
  },
  { immediate: true },
);

const contextConsent = getContextConsent();
const storeConsent = getStoreConsent(props.tenant);

const mergedConsent: ConsentMode = {
  ...defaultConsent,
  ...contextConsent,
  ...storeConsent,
};

gtag("consent", "default", mergedConsent);
decorateLinks("hippocampus.eu", "consent", getEncodedConsent(mergedConsent));

addGTMScript(props.gtmCode);

pushEventToDatalayer("pageview");

if (!contextConsent && !storeConsent) {
  show.value = true;
}

const clickAcceptAll = () => {
  consent.necessary = true;
  consent.analytics = true;
  consent.preferences = true;
  consent.marketing = true;

  updateConsent(getConsentMode());
};

const clickAcceptSome = () => {
  consent.necessary = true;

  updateConsent(getConsentMode());
};

const clickRejectAll = () => {
  consent.necessary = false;
  consent.analytics = false;
  consent.preferences = false;
  consent.marketing = false;

  updateConsent(getConsentMode());
};

function updateConsent(consent: ConsentMode) {
  gtag("consent", "update", consent);
  setStoreConsent(props.tenant, consent);
  decorateLinks("hippocampus.eu", "consent", getEncodedConsent(consent));
  show.value = false;
}

function getConsentMode(): ConsentMode {
  const consentMode: ConsentMode = {
    functionality_storage: consent.necessary ? "granted" : "denied",
    security_storage: consent.necessary ? "granted" : "denied",
    personalization: consent.preferences ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
  };
  return consentMode;
}
</script>

<style>
.gap-1 {
  gap: 0.25rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 1rem;
}
.gap-4 {
  gap: 1.5rem;
}

#cookie-consent-banner {
  display: flex;
  flex-direction: column;
  align-items: start;
  text-align: left;
  gap: 1rem;
  padding: 1.5rem 1.5em;

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 60rem;
  z-index: 2000;

  border-top: 0.05rem solid #d7dedf;
  border-right: 0.05rem solid #d7dedf;
  border-top-right-radius: 0.33rem;
  background-color: rgb(245, 245, 245);
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
}

#text {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 0.75rem;
}

#text > * {
  margin: 0;
}

#buttons {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
}

#btn-extra-information {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}

#preferences {
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 1rem;
  padding: 1rem 1.5rem;
  width: 100%;
  border-radius: 1em;
  background-color: white;
}

#preferences .preference {
  display: grid;
  align-items: start;
  grid-template-columns: 1fr 3fr;
  gap: 1rem;
}

#preferences .preference label {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  line-height: 1;
}

#preferences .preference input {
  width: 1rem;
  height: 1rem;
}

#preferences .preference .description {
  font-size: 0.8em;

  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 600px) {
  #cookie-consent-banner {
    padding: 0.5rem 0.75rem;
    border-top-right-radius: 0;
  }

  #buttons {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  #preferences {
    padding: 0.5rem 0.75rem;
  }

  #preferences .preference {
    gap: 0.5rem;
    grid-template-columns: 1fr;
  }

  #preferences .preference .description {
    margin-left: 0.2rem;
  }
}
</style>
